import React, { useState, useRef } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX } from 'lucide-react';
import { useAudio } from '../context/AudioContext';
import { useTheme } from '../context/ThemeContext';
import { Slider } from './ui/slider';
import WaveformPlayer from './WaveformPlayer';

// Fallback simple progress bar used when WaveformPlayer crashes on mobile
const SimpleProgressBar = ({ currentTime, duration, onSeek, themeColor }) => {
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX || (e.touches && e.touches[0].clientX) || 0) - rect.left;
    onSeek((x / rect.width) * duration);
  };
  return (
    <div
      className="w-full h-full flex items-center cursor-pointer"
      onClick={handleClick}
      onTouchEnd={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = (e.changedTouches[0].clientX) - rect.left;
        onSeek((x / rect.width) * duration);
      }}
    >
      <div className="relative w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-none"
          style={{ width: `${progress}%`, backgroundColor: themeColor }}
        />
      </div>
    </div>
  );
};

// Error boundary scoped to just the waveform — isolates canvas crashes
class WaveformErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false };
  }
  static getDerivedStateFromError() {
    return { crashed: true };
  }
  componentDidCatch(err) {
    console.warn('WaveformPlayer crashed, falling back to progress bar:', err);
  }
  render() {
    if (this.state.crashed) {
      const { currentTime, duration, onSeek, themeColor } = this.props;
      return <SimpleProgressBar currentTime={currentTime} duration={duration} onSeek={onSeek} themeColor={themeColor} />;
    }
    return this.props.children;
  }
}

const AudioPlayer = () => {
  const { currentTrack, isPlaying, currentTime, duration, volume, setVolume, playTrack, pause, play, seek, skipNext, skipPrev } = useAudio();
  const { currentTheme } = useTheme();
  const [isMuted, setIsMuted] = useState(false);
  const prevVolumeRef = useRef(80);

  if (!currentTrack) return null;

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleVolumeToggle = () => {
    if (isMuted) {
      setVolume(prevVolumeRef.current);
      setIsMuted(false);
    } else {
      prevVolumeRef.current = volume;
      setVolume(0);
      setIsMuted(true);
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-black/98 backdrop-blur-xl border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center gap-4">
          {/* Track Info */}
          <div className="flex items-center gap-3 min-w-[180px] max-w-[220px]">
            <img 
              src={currentTrack.imageUrl} 
              alt={currentTrack.title}
              className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
              onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=100&h=100&fit=crop'; }}
            />
            <div className="hidden sm:block overflow-hidden">
              <div className="text-white font-medium text-sm truncate">{currentTrack.title}</div>
              <div className="text-gray-400 text-xs truncate">{currentTrack.genre} {currentTrack.bpm && `• ${currentTrack.bpm} BPM`}</div>
            </div>
          </div>

          {/* Controls + Waveform */}
          <div className="flex-1 flex flex-col items-center gap-1">
            {/* Playback Controls */}
            <div className="flex items-center gap-4">
              <button 
                onClick={skipPrev}
                className="text-gray-400 hover:text-white transition-colors hover:scale-110"
                title="Previous / Restart"
              >
                <SkipBack className="w-4 h-4" />
              </button>
              
              <button
                onClick={() => isPlaying ? pause() : play()}
                className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110"
                style={{ backgroundColor: currentTheme.primary }}
              >
                {isPlaying ? (
                  <Pause className="w-4 h-4 text-black fill-black" />
                ) : (
                  <Play className="w-4 h-4 text-black fill-black ml-0.5" />
                )}
              </button>

              <button 
                onClick={skipNext}
                className="text-gray-400 hover:text-white transition-colors hover:scale-110"
                title="Next track"
              >
                <SkipForward className="w-4 h-4" />
              </button>
            </div>

            {/* Waveform + Time */}
            <div className="w-full max-w-2xl flex items-center gap-2">
              <span className="text-xs text-gray-400 w-10 text-right font-mono">{formatTime(currentTime)}</span>
              <div className="flex-1 h-10">
                <WaveformErrorBoundary currentTime={currentTime} duration={duration} onSeek={seek} themeColor={currentTheme.primary}>
                  <WaveformPlayer
                    audioUrl={currentTrack.audioUrl}
                    currentTime={currentTime}
                    duration={duration}
                    isPlaying={isPlaying}
                    onSeek={seek}
                  />
                </WaveformErrorBoundary>
              </div>
              <span className="text-xs text-gray-400 w-10 font-mono">{formatTime(duration)}</span>
            </div>
          </div>

          {/* Volume */}
          <div className="hidden md:flex items-center gap-2 min-w-[120px]">
            <button 
              onClick={handleVolumeToggle}
              className="text-gray-400 hover:text-white transition-colors"
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <Slider
              value={[volume]}
              max={100}
              step={1}
              onValueChange={(value) => {
                setVolume(value[0]);
                if (value[0] > 0 && isMuted) setIsMuted(false);
                if (value[0] === 0) setIsMuted(true);
              }}
              className="w-20"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
